// Vertical-bar primitive — FE-032 + dashboard parity v3.7.
//
// Originally a horizontal bar (file kept its old name for import
// stability and existing testids). v3.7 flips to vertical columns
// — each employee gets a tall bar, names sit underneath as the
// X-axis labels, values sit above each column. Same data shape as
// before; same drill-through; same testids the FE-032 tests look
// for.
//
// Pagination kicks in when the row set exceeds ``pageSize``
// (default 6) — Prev / Next chevrons walk through the staff list
// without compressing every name into one viewport.

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

export interface HorizontalBarRow {
  /** Stable key — typically the user_id. */
  id: string;
  /** Visible label (defaults to the id when omitted). */
  label?: ReactNode;
  /** Value driving the bar height. */
  value: number;
  /** Tail content (e.g. a metric badge) rendered below the bar. */
  trailing?: ReactNode;
  /** Optional override of the bar fill color class. */
  barClassName?: string;
  /** Optional href for the column to drill through to. */
  href?: string;
  /** Aria-label override; defaults to ``${label}: ${value}``. */
  ariaLabel?: string;
}

export interface HorizontalBarProps {
  rows: HorizontalBarRow[];
  /** Show a hint when there are no rows. */
  emptyLabel?: string;
  /** Override the visible suffix (default ``""``). */
  valueSuffix?: string;
  /** ``data-testid`` applied to the wrapper. */
  testId?: string;
  /** When ``rows.length`` exceeds this, the list paginates with
   * Prev/Next controls. Default 6 — picked so the chart stays
   * scannable on a typical viewport without requiring a scroll
   * inside the dashboard card. */
  pageSize?: number;
}

const DEFAULT_BAR = "bg-teal";
const DEFAULT_PAGE_SIZE = 6;
const COLUMN_HEIGHT = 160; // px


export function HorizontalBar({
  rows, emptyLabel, valueSuffix = "", testId,
  pageSize = DEFAULT_PAGE_SIZE,
}: HorizontalBarProps) {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [rows.length]);

  if (!rows.length) {
    return (
      <p
        data-testid={testId ? `${testId}-empty` : undefined}
        className="text-sm text-text-tertiary"
      >
        {emptyLabel ?? "No data in the current window."}
      </p>
    );
  }

  // Width scaling uses the global max so paged columns stay
  // comparable across pages.
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * pageSize;
  const visible = rows.slice(start, start + pageSize);
  const showPager = pageCount > 1;

  return (
    <div className="flex flex-col gap-3">
      <ul
        data-testid={testId}
        role="list"
        className="flex items-end justify-around gap-3 rounded-md bg-canvas/40 px-3 pt-3 pb-2"
        style={{ minHeight: COLUMN_HEIGHT + 56 }}
      >
        {visible.map((row) => {
          const heightPct = max === 0 ? 0 : (row.value / max) * 100;
          const ariaLabel =
            row.ariaLabel ??
            `${typeof row.label === "string" ? row.label : row.id}: ${row.value}${
              valueSuffix ? ` ${valueSuffix}` : ""
            }`;
          const RowEl = row.href ? "a" : "div";
          const rowProps = row.href ? { href: row.href } : {};
          return (
            <li
              key={row.id}
              className="flex min-w-0 flex-1 flex-col items-center gap-1"
            >
              <RowEl
                {...rowProps}
                data-testid={`horizontal-bar-row-${row.id}`}
                aria-label={ariaLabel}
                className={[
                  "flex w-full flex-col items-center gap-1 rounded-md px-1 py-1",
                  row.href ? "hover:bg-raised" : "",
                ].join(" ")}
              >
                {/* Value above the bar */}
                <span className="text-xs font-medium text-text-primary tabular-nums">
                  {row.value}
                  {valueSuffix ? (
                    <span className="ml-0.5 text-[10px] text-text-tertiary">
                      {valueSuffix}
                    </span>
                  ) : null}
                </span>
                {/* The column itself */}
                <span
                  className="relative flex w-full items-end justify-center overflow-hidden rounded-t-md bg-recessed/40"
                  style={{ height: COLUMN_HEIGHT }}
                >
                  <span
                    data-testid={`horizontal-bar-fill-${row.id}`}
                    className={[
                      "block w-full rounded-t-md",
                      row.barClassName ?? DEFAULT_BAR,
                    ].join(" ")}
                    style={{ height: `${heightPct}%` }}
                    aria-hidden="true"
                  />
                </span>
                {/* Name below the bar */}
                <span
                  className="w-full truncate text-center text-[11px] text-text-secondary"
                  title={typeof row.label === "string" ? row.label : undefined}
                >
                  {row.label ?? row.id}
                </span>
              </RowEl>
              {row.trailing ? (
                <span className="w-full text-center">{row.trailing}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
      {showPager && (
        <footer
          data-testid={testId ? `${testId}-pager` : undefined}
          className="flex items-center justify-between text-[11px] text-text-tertiary"
        >
          <span>
            {start + 1}–{start + visible.length} of {rows.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              data-testid={testId ? `${testId}-prev` : undefined}
              aria-label="Previous staff"
              disabled={safePage === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <ChevronLeft size={14} aria-hidden="true" />
            </button>
            <span className="tabular-nums">
              {safePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              data-testid={testId ? `${testId}-next` : undefined}
              aria-label="Next staff"
              disabled={safePage >= pageCount - 1}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="inline-flex h-6 w-6 items-center justify-center rounded text-text-secondary hover:bg-recessed disabled:cursor-not-allowed disabled:text-text-tertiary"
            >
              <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

export function shortenUserId(id: string): string {
  // The BE-029 employee_analytics shape only carries user_id (no
  // display_name); show the first eight characters so the bar isn't
  // overwhelmed by the full UUID. FE-033 introduces a user-display
  // resolver that this helper will defer to once it exists.
  return id.slice(0, 8);
}
